<template>
  <b-container fluid>
    <b-row>
      <search
        @findLicenseByChange="handleFindLicenseByChange"
        @searchInputChange="handleSearchInputChange"
        @orderDateChange="handleOrderDateChange"
        @licenseTypeChange="handleLicenseTypeChange"
        @fromDateChange="handleFromDateChange"
        @toDateChange="handleToDateChange"
        @advancedSearchButtonClick="handleAdvancedSearchButtonClick"
        @onSearchClick="handleSearchClick"
        v-bind="search"
        :isAdvancedSearchVisible="search.showAdvancedSearch"
      />
    </b-row>
    <b-row>
      <quick-find @filterClicked="handleQuickFindClick" page="License details" />
    </b-row>
    <div v-if="isInternal && !isPersonInRole">
      <a href="https://citrix.service-now.com/sp?id=sc_cat_item&sys_id=713158551b8c50500bfeb912cd4bcbab" target="_blank">
        <b-row>
          <b-col class="not-permitted-text">
            <h5>{{ $t('contactCustomerCareReturnInternal')}}</h5>
          </b-col>
        </b-row>
      </a>
    </div>
    <template v-if="hasApiCallFailed">
      <something-went-wrong :text="$t('licenseDetail')" />
    </template>
    <template v-else-if="isLoading">
      <div class="col">
        <loading />
      </div>
    </template>
    <template v-else>
      <b-row class="license-detail-container">
        <div class="col-12">
          <h3>{{ licenseObject.ProductName }}</h3>
        </div>
        <div class="col-md-12 col-lg-4">
          <b-row>
            <div class="col-sm-12 col-md-6 col-lg-12 text-center order-md-2 order-lg-1">
              <template v-if="isLicenseArchived">
                <b-button variant="primary" class="available-allocations-btn" size="lg">
                  {{ $t('undoArchive') }}
                </b-button>
              </template>
              <template v-if="license.remainingUserCount > 0 && !licenseObject.IsArchived">
                <b class="available-allocations-info">
                  {{ $t('unallocatedLicenses', {
                    count: license.remainingUserCount,
                    license: license.remainingUserCount === 1 ? $t('license') : $tc('licenses', 2)
                  }) }}
                </b>
                <b-button
                  variant="success"
                  class="available-allocations-btn"
                  size="lg"
                  @click="handleAllocateClick"
                  :disabled="isInternal && !isPersonInRole"
                >
                  {{ $t('allocateLicenses') }}
                </b-button>
              </template>
            </div>
            <div class="col-sm-12 col-md-6 col-lg-12 order-md-1 order-lg-2">
              <div class="card card-body bg-light license-info-container">
                <div>
                  <p>
                    <b>{{ $t('licenseAccessCode') }}</b><br>
                    <span>{{ license.SerialNumber }}</span>
                  </p>
                </div>
                <div>
                  <p>
                    <b>{{ $t('orderDetails') }}</b><br>
                    <span>
                      {{ $tc('orderNumber', 1) }}:&nbsp;
                      <b>{{ getOrderNumber(license) }}</b>
                    </span><br>
                    <span>{{ $t('purchased') }}: <b>{{ license.userCount }}</b></span><br>
                    <span>
                      {{ $t('available') }}:&nbsp;
                      <b class="success-text">{{ license.remainingUserCount }}</b>
                    </span><br>
                    <span>
                      {{ $t('purchaseDate') }}:&nbsp;
                      <b>{{ getReadableDate(license.deliveryDate) }}</b>
                    </span><br>
                    <span>
                      {{ $t('expirationDate') }}:&nbsp;
                      <b>{{ getReadableDate(license.expirationDate) }}</b>
                    </span><br>
                    <span>
                      {{ $t('purchasedBy') }}:&nbsp;
                      <b>{{ purchasedBy }}</b>
                    </span>
                    <br>
                  </p>
                </div>
              </div>
            </div>
          </b-row>
        </div>
        <div class="col-md-12 col-lg-8">
          <b-row>
            <div class="col-sm-12 col-md-4 d-block d-lg-none">
              <b-select
                v-model="renderOption"
                :options="renderOptions"
                size="lg"
                style="margin-bottom: 20px;"
              />
            </div>
            <div class="col-sm-12 col-md-4 col-lg-12 d-none d-lg-block">
              <span :class="getTabButtonClass('allocations')">
                <span @click="renderOption = 'allocations'">
                  {{ $t('allocations') }}
                </span>
                <div class="triangle" v-if="renderOption === 'allocations'"/>
              </span>
              <span :class="getTabButtonClass('notes')">
                <span @click="renderOption = 'notes'">
                  {{ $t('notes') }}
                </span>
                <div class="triangle" v-if="renderOption === 'notes'" />
              </span>
              <span :class="getTabButtonClass('activityLog')">
                <span @click="renderOption = 'activityLog'">
                  {{ $t('activityLog') }}
                </span>
                <div class="triangle" v-if="renderOption === 'activityLog'" />
              </span>
              <hr class="tab-underline">
            </div>
            <template v-if="shouldShowActionsDropdown">
              <div class="col-sm-12 col-md-8" >
                <b-select
                v-model="actionOption"
                :options="actionOptions"
                size="lg"
                class="action-options"
                @change.native="handleActionSelection"
                :disabled="selectedFulfillments.length === 0 || hasError || hasNoRecords || (isInternal && !isPersonInRole)"
                ref="actionDropdown"
                :title="actionsDropdownTitleText"
              />
              </div>
            </template>
          </b-row>
          <b-row v-if="renderOption === 'allocations'">
            <div class="col-sm-12" v-if="license.remainingUserCount !== license.userCount">
              <b-row class="table-header vertical-padding">
                <div class="col checkbox-col">
                  <b-checkbox
                    @change="toggleAllLicensesSelection"
                    v-model="areAllLicensesSelected"
                  />
                </div>
                <div class="col">
                  <b-row>
                    <div class="col allocate-col">{{ $t('allocated') }}</div>
                    <div class="col d-none d-md-block d-lg-block">
                      {{ $tc('hostId', 1) }}
                    </div>
                    <div class="col download-col header text-center">
                      {{ $t('licenseFile') }}
                    </div>
                  </b-row>
                </div>
              </b-row>
              <template v-for="(fulfillmentListItem, i) in fulfillmentList">
                <div class="col-12" :key="`${fulfillmentListItem.hostIDValue}${i}`">
                  <b-row :class="`table-row vertical-padding ${i % 2 === 1 ? 'odd-row' : ''}`">
                    <div class="col checkbox-col">
                      <b-checkbox
                        @change="toggleLicenseSelection(fulfillmentListItem)"
                        :checked="isLicenseSelected(fulfillmentListItem)"
                        :class="isLicenseInvalid(fulfillmentListItem) ? 'error-checkbox' : ''"
                      />
                    </div>
                    <div class="col">
                      <b-row>
                        <div class="col allocate-col">
                          {{ fulfillmentListItem.fulfilledQuantity }}
                        </div>
                        <div class="col d-none d-md-block d-lg-block">
                          {{ fulfillmentListItem.hostIDValue }}
                        </div>
                        <div class="col download-col text-center">
                          <button
                            class="download-icon-button"
                            @click="downloadLicense(fulfillmentListItem, true)"
                          >
                            <i class="fa fa-download" />
                          </button>
                        </div>
                      </b-row>
                    </div>
                  </b-row>
                </div>
              </template>
              <b-row>
                <div class="col-md-6 export-link-container">
                  <a href="#" @click.stop.prevent="handleExportClick">
                    {{ $t('exportToExcel') }}
                  </a>
                </div>
                <div class="col-md-6 text-center">
                  <div class="pagination-container">
                    <b-pagination
                      :prev-text="$t('paginationPrevText')"
                      :next-text="$t('paginationNextText')"
                      hide-goto-end-buttons
                      :total-rows="license.fulfillmentList.length"
                      :per-page="pageSize"
                      v-model="currentPage"
                    />
                  </div>
                </div>
              </b-row>
            </div>
            <div class="col-sm-12" v-else>
              <div class="empty-state-text">
                <template v-if="licenseObject.IsArchived">
                  <p>{{ $t('hiddenLicenseEmptyStateMessage') }}</p>
                </template>
                <template v-else>
                  <p>
                    {{
                      $t('licenseAllocationTextPrefix', {
                        available: license.remainingUserCount,
                        total: license.userCount,
                        license: license.userCount === 1 ? $t('license') : $tc('licenses', 2)
                      })
                    }}
                    <template v-if="license.remainingUserCount && license.userCount">
                      <span>{{ $t('licenseAllocationTextSuffix') }}</span>
                    </template>
                  </p>
                </template>
              </div>
            </div>
          </b-row>
          <b-row v-if="renderOption === 'notes'">
            <notes :licenseId="license.orderID" :AssetID="license.AssetID" />
          </b-row>
          <b-row class="remove-default-row-margin" v-if="renderOption === 'activityLog'">
            <activity-log
              :licenseId="licenseId"
              :purchaseDate="license.deliveryDate"
              :hostIdType="license.hostIDType"
            />
          </b-row>
        </div>
      </b-row>
    </template>
    <modify-allocation-modal
      :modal="modifyModal"
      :toggleModal="closeModifyModal"
      :selectedLicenses="selectedLicenses"
      :allowPartial="licenseObject.AllowpartialAllocation"
    />
    <allocation-modal
      :modal="allocateModal"
      :closeModal="closeAllocateModal"
      :openModal="openAllocateModel"
      :selectedLicenses="selectedLicenses"
      :getUpdatedLicenses="getUpdatedInfo"
    />
    <return-allocation-modal
      isFromDetailPage
      :modal="returnModal"
      :closeModal="closeReturnModal"
      :selectedLicenses="selectedLicenses"
    />
    <error-modal
      :modal="errorModal"
      :closeModal="closeErrorModal"
      :errorType="errorModalType"
      :handleCtaClick="errorModalCtaClickHandler"
    />
  </b-container>
</template>

<script>
  import Vue from 'vue'
  import { mapGetters, mapMutations } from 'vuex'
  import moment from 'moment'
  import Json2Csv from 'json2csv'
  import download from 'downloadjs'

  import Loading from './Loading'
  import SomethingWentWrong from './SomethingWentWrong'
  import Notes from './Notes'
  import ActivityLog from './ActivityLog'
  import SearchMixin from '../mixins/SearchMixin'
  import DateFormatMixin from '../mixins/DateFormatMixin'
  import AllocationModal from './AllocationModal'
  import ReturnAllocationModal from './ReturnAllocationModal'
  import ModifyAllocationModal from './ModifyAllocationModal'
  import ErrorModal from './ErrorModal'
  import SurveyModal from './SurveyModal'

  const parseCsv = Json2Csv.parse

  export default {
    name: 'License',
    mixins: [SearchMixin, DateFormatMixin],
    components: {
      'loading': Loading,
      'something-went-wrong': SomethingWentWrong,
      'notes': Notes,
      'activity-log': ActivityLog,
      'allocation-modal': AllocationModal,
      'modify-allocation-modal': ModifyAllocationModal,
      'return-allocation-modal': ReturnAllocationModal,
      'error-modal': ErrorModal,
      'f': SurveyModal
    },
    mounted () {
      this.getUpdatedInfo()
    },
    created () {
      Vue.AppInsights.trackPageView('License details page', `/licence/${this.licenseId}`)
    },
    data () {
      return {
        isInternal: window.location.host.includes('citrite.net'),
        currentPage: 1,
        pageSize: 5,
        license: {
          activationID: '',
          AssetID: '',
          productName: '',
          deliveryDate: '',
          versionDate: '',
          userCount: 0,
          remainingCount: 0,
          hostIDType: '',
          expirationDate: '',
          orderID: '',
          SAPSalesOrderNumber: '',
          licenseItemNumber: '',
          hostIDValue: '',
          isFullAllocation: '',
          isArchived: '',
          purchasedBy: '',
          fulfillmentList: []
        },
        selectedFulfillments: [],
        areAllLicensesSelected: false,
        hasError: false,
        notesInput: '',
        renderOption: 'allocations',
        renderOptions: [
          { value: 'allocations', text: this.$t('allocations') },
          { value: 'notes', text: this.$t('notes') },
          { value: 'activityLog', text: this.$t('activityLog') }
        ],
        actionOption: '',
        isLoading: true,
        hasApiCallFailed: false,
        allocateModal: false,
        modifyModal: false,
        errorModal: false,
        errorModalType: '',
        returnModal: false,
        showSurveyModalProp: false
      }
    },
    computed: {
      ...mapGetters(['isPersonInRole', 'isSurveyModalInteracted', 'isSurveyModalInteractedXS', 'showSurveyModal', 'validXSSurveyURL']),
      purchasedBy () {
        const purchasedBy = this.license.purchasedBy
        return (
          purchasedBy === null ||
          purchasedBy === 'NA' ||
          purchasedBy === 'N/A' ||
          purchasedBy === 'N.A'
        )
          ? this.$t('notAvailable')
          : purchasedBy
      },
      actionOptions () {
        const dummyOption = { value: '', text: this.$t('selectAnAction'), disabled: true }
        const actionOptions = [
          { value: 'downloadLicenseFiles', text: this.$tc('downloadLicenseFiles', 2) },
          { value: 'modifyAllocations', text: this.$t('modifyAllocations') },
          { value: 'returnAllocations', text: this.$t('returnAllocations') }
        ]
        return [dummyOption, ...actionOptions]
      },
      fulfillmentList () {
        const startIndex = (this.currentPage - 1) * this.pageSize
        const endIndex = (this.currentPage * this.pageSize)
        return this.license.fulfillmentList.slice(startIndex, endIndex)
      },
      isLicenseArchived () {
        // Write logic for finding if a license is archived here.
        // Return true or false temporarily
        return this.license.isArchived || false
      },
      isLicenseExpiring () {
        // Write logic for determining if a license is renewable here.
        // Using expiring licenses logic temporarily
        let dayDifference = moment(new Date(this.license.expirationDate)).diff(moment(), 'days') + 1
        dayDifference = Math.abs(dayDifference)
        return dayDifference < 30 || (dayDifference > 30 && dayDifference < 90)
      },
      shouldShowActionsDropdown () {
        return this.renderOption === 'allocations' &&
          this.license.remainingUserCount !== this.license.userCount
      },
      licenseId () {
        return this.$route.params.licenseId
      },
      orderNumberFromCode () {
        return this.licenseId.match(/^.*-(.*)-.*/)[1]
      },
      licenseObject () {
        return this.$store.getters.licenseDetails
      },
      loggedInUserName () {
        return this.$store.getters.authToken.userName || ''
      },
      loggedInUserPersonID () {
        return this.$store.getters.authToken.personId || ''
      },
      fulfillmentID () {
        return this.selectedFulfillments[0]
          ? this.selectedFulfillments[0].fulfillmentID
          : ''
      },
      selectedLicenses () {
        return [{
          ActivationID: this.license.activationID,
          HostType: this.license.hostIDType,
          RemainingUserCount: this.license.remainingUserCount,
          OrderID: this.license.orderID,
          SerialNumber: this.license.SerialNumber,
          AssetID: this.license.AssetID,
          LicenseItemNumber: this.license.licenseItemNumber,
          ProductName: this.licenseObject.ProductName,
          UserCount: this.license.userCount,
          FulfillmentID: this.fulfillmentID,
          Fulfillments: this.selectedFulfillments
        }]
      },
      errorModalCtaClickHandler () {
        const closeModalAndProceed = (value) => {
          this.closeErrorModal()
          this.handleActionSelection({ target: { value } })
        }
        switch (this.errorModalType) {
          case 'return':
            return () => {
              this.selectedLicenses = this.selectedLicenses
                .filter(l => l.UserCount !== l.RemainingUserCount)
              closeModalAndProceed('returnAllocations')
            }
          default:
            return () => {}
        }
      },
      actionsDropdownTitleText () {
        if (this.licenseObject.IsArchived) return this.$t('hiddenLicenseEmptyStateMessage')
        if (this.selectedFulfillments.length === 0) return this.$t('noSelectionInfoText')
        return ''
      },
      downloadRequestBody () {
        return [{
          activationID: this.license.activationID,
          customerId: this.$store.getters.person.customerId,
          hostId: this.selectedFulfillments.map(l => l.hostIDValue),
          hostType: this.license.hostIDType,
          loggedInUserName: this.loggedInUserName,
          loggedInUserPersonID: this.$store.getters.person.id,
          action: 'Redownload',
          licenseLineItemNumber: this.license.licenseItemNumber,
          productName: this.licenseObject.ProductName,
          assetID: this.license.AssetID
        }]
      }
    },
    methods: {
      ...mapMutations([
        'VALID_SURVEY_DATA',
        'SHOW_SURVEY_MODAL',
        'SHOW_SURVEY_MODAL_XS',
        'VALID_SURVEY_DATA_XS'
      ]),
      validateMultipleSelection () {
        let hostType
        if (!this.selectedFulfillments.length) return
        hostType = this.selectedFulfillments[0].hostIDType
        this.selectedFulfillments = this.selectedFulfillments.map(l => {
          if (l.hostIDType === hostType) {
            delete l.isValid
            return l
          } else {
            return { ...l, isValid: false }
          }
        })
        this.hasError = this.selectedFulfillments.some(l => l.hasOwnProperty('isValid'))
      },
      isLicenseInvalid (license) {
        return (this.isLicenseSelected(license, true) || {}).hasOwnProperty('isValid')
      },
      isLicenseSelected (license, returnItem = false) {
        const selectedLicense = this.selectedFulfillments
          .find(val => val.fulfillmentID === license.fulfillmentID)
        return returnItem ? selectedLicense : !!selectedLicense
      },
      calculateDayDifference (date, expiring = false) {
        let isDateLessThanToday, from, to, number
        number = expiring ? 0 : 1
        date = new Date(date)
        date = date.setTime(
          Date.UTC(
            date.getFullYear(), date.getMonth(), date.getDate(),
            date.getHours(), date.getMinutes(), date.getSeconds()
          )
        )
        isDateLessThanToday = Date.now() < new Date(date).getTime()
        if (moment().isSame(moment(date), 'day')) {
          return 0
        }
        if (expiring && !isDateLessThanToday) {
          return -1
        }
        from = isDateLessThanToday ? moment(date) : moment()
        to = isDateLessThanToday ? moment().subtract(1, 'days') : moment(date)
        return from.diff(to, 'days') + number
      },
      toggleAllLicensesSelection () {
        this.selectedFulfillments = this.areAllLicensesSelected
          ? []
          : [...this.fulfillmentList]
        this.validateMultipleSelection()
      },
      toggleLicenseSelection (license) {
        if (!this.selectedFulfillments.length || !this.isLicenseSelected(license)) {
          this.selectedFulfillments.push(license)
        } else {
          this.selectedFulfillments = this.selectedFulfillments
            .filter(l => l.fulfillmentID !== license.fulfillmentID)
        }
        this.areAllLicensesSelected = this.selectedFulfillments.length === this.license.fulfillmentList.length
        this.validateMultipleSelection()
      },
      handleAdvancedSearchButtonClick (showAdvancedSearch) {
        this.search.showAdvancedSearch = showAdvancedSearch
      },
      handleSearchClick () {
        Vue.AppInsights.trackEvent(
          this.search.showAdvancedSearch ? 'Advanced search' : 'Search',
          {
            eventType: 'click',
            searchInput: this.search.searchInput,
            searchType: this.search.findLicenseByOption,
            page: 'License details'
          }
        )
        this.$router.push({
          name: 'AllLicenses',
          params: {
            search: this.search,
            isAdvancedSearchVisible: this.search.showAdvancedSearch
          }
        })
      },
      getTabButtonClass (renderOption) {
        return [{
          'tab-btn': true,
          'primary-text': this.renderOption !== renderOption,
          'disabled-text': this.renderOption === renderOption
        }]
      },
      getReadableDate (date) {
        const validDate = new Date(date)
        if (Date.parse(validDate) === 'NaN') {
          return this.$t('notApplicable')
        }
        const temp = moment.utc(date).format(this.getDateFormatBasedOnLocale())
        const year = validDate.getFullYear()
        return year < 2 ? this.$t('notApplicable') : temp
      },
      handleQuickFindClick (filterName) {
        this.$router.push({
          name: 'AllLicenses',
          params: {
            quickFind: filterName
          }
        })
      },
      getOrderNumber ({ SAPSalesOrderNumber, licenseItemNumber }) {
        return !licenseItemNumber
          ? (SAPSalesOrderNumber || this.orderNumberFromCode)
          : `${SAPSalesOrderNumber || this.orderNumberFromCode}/${licenseItemNumber}`
      },
      handleExportClick () {
        var data = this.license.fulfillmentList.map((this.cleanObject))
        const fields = data.length > 0 ? Object.keys(data[0]) : []
        try {
          download(
            parseCsv(data, { fields }),
            'licenses.csv',
            'application/octet-stream'
          )
        } catch (error) {
          this.loggly.log({
            errorMessage: 'Error parsing CSV in license details page',
            errorObject: error,
            errorStack: error.stack
          })
        }
      },
      cleanObject (objectToClean) {
        return {
          fulfillmentID: this.cleanData(objectToClean.fulfillmentID),
          hostIDValue: this.cleanData(objectToClean.hostIDValue),
          hostIDType: this.cleanData(objectToClean.hostIDType),
          fulfilledQuantity: this.cleanData(objectToClean.fulfilledQuantity),
          parentFulfillmentID: this.cleanData(objectToClean.parentFulfillmentID)
        }
      },
      cleanData (dataToClean) {
        var checkRegex = '^[=+\\-@].*'
        return dataToClean.length > 0 ? (dataToClean.match(checkRegex) ? '\'' + dataToClean : dataToClean) : dataToClean
      },
      handleActionSelection ({ target: { value: action } }) {
        switch (action) {
          case 'downloadLicenseFiles':
            this.downloadLicense()
            break
          case 'modifyAllocations':
            this.modifyModal = true
            break
          case 'returnAllocations':
            if (this.selectedLicenses.some(l => l.UserCount === l.RemainingUserCount)) {
              this.errorModal = true
              this.errorModalType = 'return'
              break
            }
            if (this.selectedLicenses.length > 0) {
              this.returnModal = true
            }
            break
          default:
            break
        }
      },
      resetActionOption (clearLicenses = true) {
        if (clearLicenses) {
          this.selectedFulfillments = []
        }
        this.areAllLicensesSelected = false
        this.actionOption = ''
        if (this.$refs.actionDropdown) {
          this.$refs.actionDropdown.localValue = ''
        }
      },
      downloadLicense (licence, directDownload = false) {
        if (directDownload) {
          this.selectedFulfillments = [licence]
        }
        this.bottle
          .post(
            this.UrlBuilder.getDownloadUrl(), this.downloadRequestBody, {
              responseType: 'arraybuffer'
            }
          )
            .then(({ data, headers }) => {
              const fileName = this.getDownloadFilename(headers['content-disposition'])
              Vue.AppInsights.trackEvent('Download action', this.downloadRequestBody)
              switch (headers['content-type']) {
                case 'application/zip':
                  const link = document.createElement('a')
                  link.href = window.URL.createObjectURL(
                    new Blob([data], { type: 'application/zip' })
                  )
                  link.download = fileName
                  link.click()
                  break
                case 'text/html':
                  download(data, fileName, 'text/html')
                  break
                default:
                  break
              }
              this.showModal = true
              this.resetActionOption()
            })
            .catch((error) => {
              this.loggly.log({
                errorMessage: 'License download failed',
                errorObject: error,
                errorStack: error.stack
              })
            })
      },
      handleAllocateClick () {
        console.log('handle allocate click', this.showModal)
        console.log('selectedLicenses: ', this.selectedLicenses)
        if (this.selectedLicenses.some(item => item.ProductName.includes('XenServer')) && this.validXSSurveyURL) {
          if (!this.isSurveyModalInteractedXS) {
            this.SHOW_SURVEY_MODAL_XS(true)
            this.SHOW_SURVEY_MODAL(false)
            this.allocateModal = false
          } else {
            this.allocateModal = true
            this.SHOW_SURVEY_MODAL_XS(false)
            this.SHOW_SURVEY_MODAL(false)
          }
        } else {
          this.allocateModal = true
          this.SHOW_SURVEY_MODAL(false)
          this.SHOW_SURVEY_MODAL_XS(false)
        }
      },
      closeAllocateModal () {
        this.allocateModal = false
        this.resetActionOption()
        this.getUpdatedInfo()
      },
      openAllocateModel () {
        this.allocateModal = true
        this.SHOW_SURVEY_MODAL_XS(false)
        this.SHOW_SURVEY_MODAL(false)
      },
      closeReturnModal () {
        this.returnModal = false
        this.resetActionOption()
        this.getUpdatedInfo()
      },
      closeModifyModal () {
        this.modifyModal = false
        this.resetActionOption()
        this.getUpdatedInfo()
      },
      getUpdatedInfo () {
        const { licenseId } = this.$route.params
        this.isLoading = true
        this.bottle.post(this.UrlBuilder.allLicensesUrl, {
          CustomerID: this.$store.getters.person.customerId,
          Duration: 0,
          NoOfItems: 0,
          PageNumber: 1,
          PageIndex: 0
        })
          .then(() => this.bottle.get(this.UrlBuilder.licenseDetailUrl(
            this.$store.getters.person.customerId,
            this.licenseId
          )))
          .then(({ data }) => {
            this.license = data
            this.isLoading = false
          })
          .catch((error) => {
            this.isLoading = false
            this.hasApiCallFailed = true
            this.loggly.log({
              errorMessage: `Failed to fetch license with ID: ${licenseId}`,
              errorObject: error,
              errorStack: error.stack
            })
          })
      },
      getDownloadFilename (string) {
        return string.split('; ')[1].split('=')[1]
      },
      closeErrorModal () {
        this.errorModal = false
        this.resetActionOption(false)
      }
    }
  }
</script>

<style lang="scss">
  @import '../styles/variables.scss';

  h3 {
    font-weight: 400;
  }
  a {
    text-decoration: none;
    color: $primary;
  }
  .available-allocations-info {
    color: $success;
    font-size: 1.3em;
  }
  .available-allocations-btn {
    width: 100%;
    margin-bottom: 30px;
    margin-top: 5px;
  }
  .license-info-container {
    margin-top: 10px;
    margin-bottom: 30px;
  }
  .tab-btn {
    display: inline-block;
    padding-right: 20px;
    font-size: 1.3em;
    text-align: center;
    position: relative;
    cursor: pointer;
  }
  .triangle {
    width: 20px;
    height: 20px;
    background-color: $white;
    border-left: 1px solid transparent;
    border-top: 1px solid transparent;
    border-bottom: 1px solid $lightGray;
    border-right: 1px solid $lightGray;
    position: absolute;
    left: 50%;
    margin-left: -20px;
    top: 32px;
    transform: rotate(45deg)
  }
  .tab-underline {
    margin-top: 10px;
    border-bottom: 1px solid $lightGray;
    margin-bottom: 30px;
  }
  .action-options,
  .action-options:active,
  .action-options:focus {
    background-color: $primary;
    color: white;
    max-width: 300px;
    font-size: 1em;
    outline: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }
  .action-options[disabled] {
    background-color: $disabledGray;
    color: white;
    cursor: not-allowed;
  }
  .table-header {
    margin: 10px 0;
    background-color: $lightGray;
  }
  .vertical-padding {
    padding-top: 10px;
    padding-bottom: 10px;
    min-height: 50px;
  }
  .table-row {
    margin-bottom: 10px;
    border: 1px solid $lightGray;
    border-radius: 5px;
  }
  .odd-row {
    background-color: $lightPrimary;
  }
  .notes-input {
    margin-bottom: 30px;
    min-height: 150px;
  }
  .activity-log-container {
    border: 1px solid $lightGray;
    padding-top: 20px;
    padding-bottom: 20px;
    margin-bottom: 30px;
  }
  .disabled-text {
    color: $darkGray;
    cursor: default;
  }
  .primary-text {
    color: $primary;
  }
  .empty-state-text {
    margin-top: 10px;
    padding: 10px;
    background-color: $lightGray;
    font-size: 1.2em;
  }
  .export-link-container {
    padding-top: 15px;
    margin-bottom: 20px;
    text-align: center;
  }
  .pagination-container {
    display: inline-block;
  }
  .page-item.disabled .page-link {
    background-color: $lightGray !important;
    color: $white !important;
  }
  .remove-default-row-margin {
    margin-left: 0;
    margin-right: 0;
  }
  .success-text {
    color: $success;
  }
  .license-detail-container {
    margin-left: -30px;
    margin-right: -30px;
  }
  .checkbox-col {
    flex: 0 0 16px;
  }
  .download-icon-button {
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    cursor: pointer;
  }
  .allocate-col {
    text-indent: -10px;
  }
  .download-col.header {
    line-height: 16px;
  }
  .download-col {
    flex: 0 0 50px;
    text-align: right;
  }
  .zh-CN .download-col,
  .ko-KR .download-col {
    flex: 0 0 140px;
  }
  .jp-JP .download-col {
    flex: 0 0 180px;
  }
  .not-permitted-text {
      border-radius: 5px;
      margin: 10px 0 10px 0;
      padding: 20px 10px 20px 10px;
      background-color: red;
      color: white;
    }
  @media (min-width: 768px) {
    .export-link-container {
      text-align: left;
    }
    .pagination-container {
      display: block;
      float: right;
    }
    .allocate-col {
      flex: 0 0 150px;
    }
  }
</style>




// WEBPACK FOOTER //
// src/components/License.vue